import { forecastDateKey } from '../utils/forecast';

const API_KEY = import.meta.env.VITE_OPENWEATHER_API_KEY;
const BASE_URL = 'https://api.openweathermap.org';

export async function getCoordinatesFromZip(zipCode) {
  const response = await fetch(
    `${BASE_URL}/geo/1.0/zip?zip=${zipCode},US&appid=${API_KEY}`
  );

  if (!response.ok) {
    throw new Error('Invalid ZIP code');
  }

  const data = await response.json();
  return {
    lat: data.lat,
    lon: data.lon,
    city: data.name,
  };
}

async function fetchUVI(lat, lon) {
  try {
    const res = await fetch(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=uv_index&forecast_days=1`
    );
    if (!res.ok) return null;
    const d = await res.json();
    return d.current?.uv_index ?? null;
  } catch {
    return null;
  }
}

export async function getCurrentWeather(lat, lon, { includeUvi = true } = {}) {
  const [response, uvi] = await Promise.all([
    fetch(`${BASE_URL}/data/2.5/weather?lat=${lat}&lon=${lon}&units=imperial&appid=${API_KEY}`, { signal: AbortSignal.timeout(12000) }),
    includeUvi ? fetchUVI(lat, lon) : null,
  ]);

  if (!response.ok) {
    throw new Error('Failed to fetch weather data');
  }

  const data = await response.json();

  return {
    temp: Math.round(data.main.temp),
    feelsLike: Math.round(data.main.feels_like),
    tempMin: Math.round(data.main.temp_min),
    tempMax: Math.round(data.main.temp_max),
    humidity: data.main.humidity,
    pressure: data.main.pressure,
    windSpeed: Math.round(data.wind.speed),
    windDeg: data.wind.deg,
    description: data.weather[0].description,
    icon: data.weather[0].icon,
    main: data.weather[0].main,
    visibility: data.visibility ?? null, // meters (max 10,000)
    clouds: data.clouds?.all ?? 0,       // percent
    sunrise: data.sys?.sunrise ?? null,  // unix timestamp
    sunset: data.sys?.sunset ?? null,    // unix timestamp
    tzOffset: data.timezone ?? 0,        // seconds offset from UTC
    observedAt: data.dt ? new Date(data.dt * 1000) : null,
    uvi,                                 // UV index from open-meteo
  };
}

export async function getForecast(lat, lon) {
  const response = await fetch(
    `${BASE_URL}/data/2.5/forecast?lat=${lat}&lon=${lon}&units=imperial&appid=${API_KEY}`,
    { signal: AbortSignal.timeout(12000) }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch forecast data');
  }

  const data = await response.json();

  // Process hourly data (next 24 hours = 8 entries, 3-hour intervals)
  const hourly = data.list.slice(0, 8).map((item) => ({
    time: new Date(item.dt * 1000),
    temp: Math.round(item.main.temp),
    icon: item.weather[0].icon,
    description: item.weather[0].description,
    rainMm: item.rain?.['3h'] ?? 0,
    snowMm: item.snow?.['3h'] ?? 0,
    humidity: item.main.humidity,
    pressure: item.main.pressure,
    windSpeed: Math.round(item.wind.speed),
    windDeg: item.wind.deg,
    clouds: item.clouds?.all ?? 0,
    visibility: item.visibility ?? null,
    pop: item.pop ?? null,
  }));

  // Process daily forecast (group by day)
  const dailyMap = new Map();

  data.list.forEach((item) => {
    const date = forecastDateKey(new Date(item.dt * 1000), data.city?.timezone ?? 0);

    if (!dailyMap.has(date)) {
      dailyMap.set(date, {
        date: new Date(item.dt * 1000),
        temps: [],
        icons: [],
        descriptions: [],
      });
    }

    const day = dailyMap.get(date);
    day.temps.push(item.main.temp);
    day.icons.push(item.weather[0].icon);
    day.descriptions.push(item.weather[0].description);
  });

  const daily = Array.from(dailyMap.values())
    .slice(0, 5)
    .map((day) => ({
      date: day.date,
      high: Math.round(Math.max(...day.temps)),
      low: Math.round(Math.min(...day.temps)),
      icon: day.icons[Math.floor(day.icons.length / 2)],
      description: day.descriptions[Math.floor(day.descriptions.length / 2)],
    }));

  return { hourly, daily, tzOffset: data.city?.timezone ?? 0 };
}

export async function getWeatherByZip(zipCode) {
  const location = await getCoordinatesFromZip(zipCode);
  const weather = await getCurrentWeather(location.lat, location.lon);
  const forecast = await getForecast(location.lat, location.lon);

  return {
    location,
    weather,
    hourly: forecast.hourly,
    daily: forecast.daily,
  };
}

export async function getCityFromCoords(lat, lon) {
  const response = await fetch(
    `${BASE_URL}/geo/1.0/reverse?lat=${lat}&lon=${lon}&limit=1&appid=${API_KEY}`
  );

  if (!response.ok) {
    throw new Error('Failed to get location name');
  }

  const data = await response.json();
  return data[0]?.name || 'Unknown location';
}

export async function searchCities(query) {
  const response = await fetch(
    `${BASE_URL}/geo/1.0/direct?q=${encodeURIComponent(query)}&limit=5&appid=${API_KEY}`
  );

  if (!response.ok) return [];

  const data = await response.json();
  return data.map((item) => ({
    lat: item.lat,
    lon: item.lon,
    city: item.name,
    state: item.state || '',
    country: item.country || '',
  }));
}

export async function getCoordinatesFromCity(cityName) {
  const response = await fetch(
    `${BASE_URL}/geo/1.0/direct?q=${encodeURIComponent(cityName)}&limit=1&appid=${API_KEY}`
  );

  if (!response.ok) {
    throw new Error('Failed to look up city');
  }

  const data = await response.json();
  if (data.length === 0) {
    throw new Error('City not found');
  }

  return {
    lat: data[0].lat,
    lon: data[0].lon,
    city: data[0].name,
  };
}

export async function getWeatherByCity(cityName) {
  const location = await getCoordinatesFromCity(cityName);
  const weather = await getCurrentWeather(location.lat, location.lon);
  const forecast = await getForecast(location.lat, location.lon);

  return {
    location,
    weather,
    hourly: forecast.hourly,
    daily: forecast.daily,
  };
}

export async function getWeatherByCoords(lat, lon) {
  const city = await getCityFromCoords(lat, lon);
  const weather = await getCurrentWeather(lat, lon);
  const forecast = await getForecast(lat, lon);

  return {
    location: { lat, lon, city },
    weather,
    hourly: forecast.hourly,
    daily: forecast.daily,
  };
}
